import React from 'react';
import { motion } from 'framer-motion';

interface SectionHeadingProps {
  eyebrow: string;
  title: string;
  description?: string;
  centered?: boolean;
  className?: string;
}

/**
 * SectionHeading Component
 * 
 * A consistent, animated heading component for page sections
 * with support for eyebrow text, main title, and optional description.
 */
const SectionHeading: React.FC<SectionHeadingProps> = ({
  eyebrow,
  title,
  description,
  centered = false,
  className = ''
}) => {
  // Detect mobile for responsive animations
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Performance-optimized animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.2,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: isMobile ? 0.4 : 0.6,
        ease: "easeOut" 
      }
    }
  };
  
  return (
    <motion.div
      className={`${centered ? 'text-center' : ''} ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-5%" }}
      variants={containerVariants}
    >
      <motion.div
        variants={itemVariants}
        className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-medium mb-3"
      >
        {eyebrow}
      </motion.div>
      
      <motion.h2
        variants={itemVariants}
        className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 dark:text-white"
      >
        {title}
      </motion.h2>
      
      {description && (
        <motion.p
          variants={itemVariants}
          className="text-gray-600 dark:text-gray-300 max-w-2xl mb-6"
        >
          {description}
        </motion.p>
      )}
    </motion.div>
  );
};

export default SectionHeading;
